import cn from "classnames";
import { formatNumber } from "helpers/system";
import React from "react";

const indexLetters = ["A", "B", "C", "D"];

const formatRubles = (value) => `${formatNumber(value, { maximumFractionDigits: 0 })} ₽`;

const formatSignedRublesWithPercent = (value, percent) => {
    const valueSign = value > 0 ? "+" : "";
    const percentSign = percent > 0 ? "+" : "";

    return `${valueSign}${formatRubles(value)} (${percentSign}${formatNumber(percent, {
        maximumFractionDigits: 2,
    })}%)`;
};

const formatSignedRublesPerLiter = (value) => {
    const sign = value > 0 ? "+" : "";

    return `${sign}${formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} ₽/л`;
};

const getPriceRange = (index, totalCount, item) => {
    if (item.minPrice == null || item.maxPrice == null) {
        return "—";
    }

    if (index === 0) {
        return `< ${item.maxPrice}`;
    }

    return index === totalCount - 1 ? `≥ ${item.minPrice}` : `${item.minPrice} - ${item.maxPrice}`;
};

const getPercent = (item) => (item.percent == null ? "—" : `${item.percent}%`);

const getDiff = (item) => {
    if (item.diff == null) {
        return "-";
    }
    return item.diff === 0 ? "0%" : item.diff > 0 ? `+${item.diff}%` : `${item.diff}%`;
};

export default function EveryWeekRefuelingStatistic(props) {
    const { data } = props;
    const financialEfficiency = data.financialEfficiency;

    return (
        <div className="pb-2">
            <div className="a-report__text-block">
                <div className="a-report__title">Еженедельный отчет по заправкам</div>
                <div className="pb-2">
                    Отчет ниже отражает вовлеченность компании и возможности для улучшения работы
                </div>
                <div className="pb-2">
                    Период: <strong>Неделя {data.week}</strong>
                </div>
                <div className="pb-2">
                    Компания: <strong>{data.organizationName}</strong>
                </div>
                <div className="pb-2">
                    <span>
                        Общий объем заправок: <strong>{formatNumber(data.totalLoadVolume)} л.</strong> (Возможна
                        погрешность из-за влияния РЭБ)
                    </span>
                </div>
                <div>
                    <span>
                        Количество машин, участвовавших в расчёте: <strong>{data.carsCount} ТС</strong>
                    </span>
                </div>
            </div>
            <br />
            <div className="a-report__text-block no-page-break">
                <div className="a-report__text-link pb-2 font-size-18">Распределение объёма по ценовым диапазонам</div>
                <table className="a-report__table">
                    <thead>
                        <tr>
                            <th className="a-report__table--noborder-left a-report__table--noborder-top">Диапазон</th>
                            <th className="a-report__table--noborder-top a-report__table--noborder-right">
                                Интервал цен (р/л.)
                            </th>
                            <th className="a-report__table--noborder-top a-report__table--noborder-right">
                                Доля заправок
                            </th>
                            <th className="a-report__table--noborder-top a-report__table--noborder-right">
                                Отклонение к прошлой неделе
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.data.map((item, i) => (
                            <tr key={i}>
                                <td className="a-report__table--noborder-left">{indexLetters[i]}</td>
                                <td>{getPriceRange(i, data.data.length, item)}</td>
                                <td>{getPercent(item)}</td>
                                <td className="a-report__table--noborder-right">
                                    <span
                                        className={cn({
                                            "a-report__table--green": [0, 1].includes(i) && item.diff > 0,
                                            "a-report__table--red": [0, 1].includes(i) && item.diff < 0,
                                        })}
                                    >
                                        {getDiff(item)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {financialEfficiency && (
                <>
                    <br />
                    <div className="a-report__text-block no-page-break">
                        <div className="a-report__text-link pb-2 font-size-18">
                            Итоговая эффективность топливного модуля
                        </div>
                        <table className="a-report__table">
                            <thead>
                                <tr>
                                    <th className="a-report__table--noborder-left a-report__table--noborder-top">
                                        Показатель
                                    </th>
                                    <th className="a-report__table--noborder-top a-report__table--noborder-right">
                                        Значение
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="a-report__table--noborder-left">
                                        Фактическая стоимость затрат на топливо за текущий период
                                    </td>
                                    <td className="a-report__table--noborder-right">
                                        {formatRubles(financialEfficiency.totalPrice)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="a-report__table--noborder-left">
                                        Стоимость текущих затрат на топливо, приведённая к распределению
                                        пролива по диапазонам АЗС первого месяца
                                    </td>
                                    <td className="a-report__table--noborder-right">
                                        {formatRubles(financialEfficiency.totalPriceWithInitialShares)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="a-report__table--noborder-left">Экономический эффект</td>
                                    <td className="a-report__table--noborder-right">
                                        <span
                                            className={cn({
                                                "a-report__table--green": financialEfficiency.diffPrice > 0,
                                                "a-report__table--red": financialEfficiency.diffPrice < 0,
                                            })}
                                        >
                                            {formatSignedRublesWithPercent(
                                                financialEfficiency.diffPrice,
                                                financialEfficiency.diffPriceInPercent,
                                            )}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="a-report__table--noborder-left">
                                        Экономия руб/литр при использовании Топливного модуля
                                    </td>
                                    <td className="a-report__table--noborder-right">
                                        <span
                                            className={cn({
                                                "a-report__table--green": financialEfficiency.diffPricePerLiter > 0,
                                                "a-report__table--red": financialEfficiency.diffPricePerLiter < 0,
                                            })}
                                        >
                                            {formatSignedRublesPerLiter(financialEfficiency.diffPricePerLiter)}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="pt-2 pb-1">
                            Экономия рассчитана путём распределения текущего объема литров по диапазонам первоначального
                            периода с учётом актуальных цен. Показатель ₽/л показывает эффект в пересчёте на 1 литр
                            топлива и позволяет сравнивать эффективность при разном объёме заправок.
                        </div>
                    </div>
                </>
            )}
            <br />
            <div className="a-report__text-block no-page-break">
                <div className="a-report__text-link pb-2 font-size-18">Распределение объёма по ценовым диапазонам</div>
                <div className="pb-2">Как формируется отчёт:</div>
                <div className="pb-2">
                    Этот отчёт рассчитывается на основании данных топливного планировщика CARGO.RUN.
                </div>
                <div>
                    <ul className="mb-0">
                        <li>
                            Все АЗС в системе ранжируются по цене дизельного топлива, и диапазон цен делится на
                            четыре категории: <strong>А-D</strong> (от самых дешёвых к самым дорогим).
                        </li>
                        <li>Данные обновляются каждую неделю с учётом реальных цен на АЗС по всей стране.</li>
                        <li>
                            В отчёте отображается, <strong>какая доля заправок</strong> компании пришлась на каждый
                            ценовой диапазон и как ситуация изменилась по сравнению с прошлой неделей.
                        </li>
                        <li>
                            Цель планировщика — увеличить <strong>долю закупок</strong> в диапазонах A и B, обеспечивая
                            экономию при сохранении стабильных маршрутов и графиков.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
